import { useCallback } from "react";
import { useNotification } from "../core";
import { downloadBlobPart } from "../downloadUtils";
import { exportToExcel, ExportToExcelProps } from "./exportToExcel";
import { MimicExcelColumn } from "./types";

export type ExportToExcelHookProps<T> = {
  getItems: () => T[] | Promise<T[]>,
  columns?: MimicExcelColumn<T>[],
  getColumns?: () => MimicExcelColumn<T>[],
  fileName?: string,
  additionalProps?: ExportToExcelProps,
  messageKey?: string,
}

export function useExportToExcel<T extends Object>(
  {
    getItems,
    columns,
    getColumns,
    fileName,
    additionalProps,
    messageKey = "exportToExcel",
  }: ExportToExcelHookProps<T>) {
  if (!columns && !getColumns) {
    throw new Error("[useExportToExcel] Required one of properties: columns|getColumns");
  }

  const { push } = useNotification();

  return useCallback(async () => {
    const messageProps = push({
      messageKey,
      type: "info",
      autoClose: false,
      children: "Exporting to xlsx",
    });
    await new Promise(resolve => setTimeout(resolve, 0));

    push({
      ...messageProps,
      type: "info",
      supportingText: "Loading data",
    });

    const items = await getItems();

    push({
      ...messageProps,
      type: "info",
      supportingText: "Generating file",
    });

    const xlsxFile = await exportToExcel(items, (getColumns ? getColumns() : columns) || [], additionalProps);

    push({
      ...messageProps,
      type: "success",
      autoClose: true,
      supportingText: "Finished",
    });

    downloadBlobPart(
      xlsxFile,
      `${fileName || "Exported"}.xlsx`,
      "application/octet-stream",
    );
  }, [ push, getItems, columns, getColumns, fileName, additionalProps ]);
}
